import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { forkJoin, Observable, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import {
  DealResponse,
  DealCycle,
  DealContactWithDetails,
  DealContactRoleResponse,
  DealBusinessType,
  DealPriority,
} from '../models/deal.model';
import { ModalComponent } from '../../../shared/components/ui/modal/modal.component';
import { BadgeComponent } from '../../../shared/components/ui/badge/badge.component';
import { QuickActionsMenuComponent } from '../../../shared/components/quick-actions-menu/quick-actions-menu.component';
import { ContactoApiService } from '../../contactos/services/contacto-api.service';
import { EmpresaApiService } from '../../empresas/services/empresa-api.service';
import { DealContactApiService } from '../services/deal-contact-api.service';
import { LeadApiService } from '../../leads/services/lead-api.service';
import { AuthService } from '../../auth-pages/services/auth.service';
import { AuthApiService } from '../../auth-pages/services/auth-api.service';
import { DirectoryUser } from '../../../shared/services/user-directory-api.service';
import { CompanyResponse } from '../../empresas/models/empresa.model';
import { Lead } from '../../leads/models/lead.model';
import { User } from '../../auth-pages/models/user.model';
import { DatetimeService } from '../../../shared/services/datetime.service';
import { AuditApiService } from '../../audit/services/audit-api.service';
import { AuditTimelineComponent } from '../../../shared/components/audit/audit-timeline.component';
import { WorkflowRecordHistoryComponent } from '../../automation/workflows/workflow-record-history/workflow-record-history.component';
import { AuditLogResponse } from '../../audit/models/audit.model';
import { QuoteApiService } from '../../quotes/services/quote-api.service';
import { QuoteFormModalService } from '../../quotes/services/quote-form-modal.service';
import { QuoteDetailModalService } from '../../quotes/services/quote-detail-modal.service';
import { QuoteResponse } from '../../quotes/models/quote.model';

type BadgeColor =
  | 'primary'
  | 'success'
  | 'error'
  | 'warning'
  | 'info'
  | 'light'
  | 'dark';

@Component({
  selector: 'app-deal-detail-modal',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ModalComponent,
    BadgeComponent,
    QuickActionsMenuComponent,
    AuditTimelineComponent,
    WorkflowRecordHistoryComponent,
  ],
  templateUrl: './deal-detail-modal.component.html',
})
export class DealDetailModalComponent implements OnInit, OnDestroy, OnChanges {
  @Input() isOpen = false;
  @Input() deal: DealResponse | null = null;
  @Input() dealCycles: DealCycle[] = [];
  @Input() dealBusinessTypes: DealBusinessType[] = [];
  @Input() dealPriorities: DealPriority[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() dealUpdated = new EventEmitter<void>();

  // Datos de empresa
  company: CompanyResponse | null = null;
  loadingCompany = false;

  // Datos de lead vinculado
  lead: Lead | null = null;
  loadingLead = false;
  loadingUsers = false;

  // Deal-Contact junction data
  dealContacts: DealContactWithDetails[] = [];
  loadingContacts = false;
  contactsError = '';
  contactRoles: DealContactRoleResponse[] = [];

  // Owner user
  ownerUser: DirectoryUser | null = null;
  loadingOwner = false;
  registeredByUser: any = null;

  // Cotizaciones del negocio (pestaña "Cotizaciones")
  dealQuotes: QuoteResponse[] = [];
  loadingQuotes = false;
  quotesError = '';
  private quotesLoaded = false;
  private quoteSavedSub?: Subscription;

  // Audit timeline
  activeTab: 'info' | 'timeline' | 'quotes' | 'automatizaciones' = 'info';
  timelineLogs: AuditLogResponse[] = [];
  loadingTimeline = false;
  timelineError = '';
  timelinePage = 0;
  timelineTotalPages = 0;
  timelineTotalElements = 0;

  constructor(
    private contactoApi: ContactoApiService,
    private empresaApi: EmpresaApiService,
    private dealContactApi: DealContactApiService,
    private leadApi: LeadApiService,
    private authService: AuthService,
    private authApiService: AuthApiService,
    private datetimeService: DatetimeService,
    private auditApi: AuditApiService,
    private quoteApi: QuoteApiService,
    private quoteFormModal: QuoteFormModalService,
    private quoteDetailModal: QuoteDetailModalService,
  ) {}

  ngOnInit(): void {
    // Tras crear/guardar una cotización (desde el botón "Nueva cotización" de la sección o
    // cualquier otra vía), refrescar la lista de cotizaciones del negocio si la pestaña ya cargó.
    this.quoteSavedSub = this.quoteFormModal.saved$.subscribe(() => {
      if (this.deal && (this.quotesLoaded || this.activeTab === 'quotes')) {
        this.cargarCotizaciones(this.deal.id);
      }
    });
  }

  ngOnDestroy(): void {
    this.quoteSavedSub?.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['deal']) {
      if (this.deal) {
        this.loadCompany();
        this.cargarContactos(this.deal.id);
        this.cargarRoles();
        this.loadOwner();
        if (this.deal.idLead) {
          this.loadLead();
        } else {
          this.lead = null;
        }
      } else {
        // Reset on close
        this.dealContacts = [];
        this.contactsError = '';
        this.contactRoles = [];
        this.company = null;
        this.ownerUser = null;
        this.lead = null;
        this.activeTab = 'info';
        this.timelineLogs = [];
        this.timelineError = '';
        this.timelinePage = 0;
        this.dealQuotes = [];
        this.quotesError = '';
        this.quotesLoaded = false;
      }
    }
  }

  private loadOwner(): void {
    this.ownerUser = null;
    if (!this.deal?.userPropietyId) return;

    // If it's the current user, use cached value
    const currentUser = this.authService.getCurrentUser();
    if (currentUser?.id === this.deal.userPropietyId) {
      this.ownerUser = this.aDirectoryUser(currentUser);
      return;
    }

    this.loadingOwner = true;
    this.authApiService.getUserById(this.deal.userPropietyId).subscribe({
      next: (user) => {
        this.ownerUser = user;
        this.loadingOwner = false;
      },
      error: () => {
        this.loadingOwner = false;
      },
    });
  }

  /**
   * El usuario en sesión al shape del directorio (`mail`, no `email`), para que la rama que lo
   * resuelve desde memoria y la que lo pide al backend devuelvan exactamente lo mismo.
   */
  private aDirectoryUser(user: User | null): DirectoryUser | null {
    return user
      ? { id: user.id, fullname: user.fullname, lastname: user.lastname, mail: user.email }
      : null;
  }

  private cargarUsuarios(): void {
    const registerId = this.lead?.userRegisterId;
    const ownerId = this.lead?.userPropietyId;

    if (!registerId && !ownerId) return;

    this.loadingUsers = true;
    this.registeredByUser = null;
    this.ownerUser = null;

    const currentUser = this.authService.getCurrentUser();
    const sessionUser = this.aDirectoryUser(currentUser);
    const resolve = (id: number | undefined): Observable<DirectoryUser | null> =>
      id && id === currentUser?.id
        ? of(sessionUser)
        : id
          ? this.authApiService.getUserById(id)
          : of(null);

    if (registerId === ownerId) {
      resolve(registerId).subscribe({
        next: (user) => {
          this.registeredByUser = user;
          this.ownerUser = user;
          this.loadingUsers = false;
        },
        error: () => {
          this.loadingUsers = false;
        },
      });
    } else {
      forkJoin([resolve(registerId), resolve(ownerId)]).subscribe({
        next: ([registerUser, ownerUser]) => {
          this.registeredByUser = registerUser;
          this.ownerUser = ownerUser;
          this.loadingUsers = false;
        },
        error: () => {
          this.loadingUsers = false;
        },
      });
    }
  }

  private loadCompany(): void {
    this.company = null;

    if (this.deal?.idCompany) {
      this.loadingCompany = true;
      this.empresaApi.getCompanyById(this.deal.idCompany).subscribe({
        next: (company) => {
          this.company = company;
          this.loadingCompany = false;
        },
        error: () => {
          this.loadingCompany = false;
        },
      });
    }
  }

  private loadLead(): void {
    this.lead = null;
    if (!this.deal?.idLead) return;
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.loadingLead = true;
    this.leadApi.getLeadById(this.deal.idLead, businessId).subscribe({
      next: (lead) => {
        this.lead = lead;
        this.loadingLead = false;
      },
      error: () => {
        this.loadingLead = false;
      },
    });
  }

  cargarRoles(): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.dealContactApi.getActiveRoles(businessId).subscribe({
      next: (roles) => {
        this.contactRoles = roles;
        // Re-enrich role names if contacts are already loaded
        if (this.dealContacts.length > 0) {
          this.dealContacts = this.dealContacts.map((dc) => ({
            ...dc,
            roleName: this.getRoleName(dc.idDealContactRole),
          }));
        }
      },
      error: () => {
        this.contactRoles = [];
      },
    });
  }

  cargarContactos(idDeal: number): void {
    const user = this.authService.getCurrentUser();
    const businessId = user?.businessId || 1;

    this.loadingContacts = true;
    this.contactsError = '';
    this.dealContacts = [];

    this.dealContactApi.getByDeal(idDeal, businessId).subscribe({
      next: (associations) => {
        if (associations.length === 0) {
          this.loadingContacts = false;
          return;
        }

        const contactRequests = associations.map((assoc) =>
          this.contactoApi
            .getContactById(assoc.idContact)
            .pipe(catchError(() => of(null))),
        );

        forkJoin(contactRequests).subscribe({
          next: (contacts) => {
            this.dealContacts = associations.map((assoc, index) => {
              const c = contacts[index];
              return {
                ...assoc,
                contactFullname: c?.fullname,
                contactLastname: c?.lastname,
                contactMail: c?.mail,
                contactPhone: c?.phone,
                roleName: this.getRoleName(assoc.idDealContactRole),
              } as DealContactWithDetails;
            });
            this.loadingContacts = false;
          },
          error: () => {
            this.contactsError = 'Error al cargar datos de contactos';
            this.loadingContacts = false;
          },
        });
      },
      error: () => {
        this.contactsError = 'Error al cargar contactos del deal';
        this.loadingContacts = false;
      },
    });
  }

  getRoleName(idRole?: number): string {
    if (!idRole) return '';
    const role = this.contactRoles.find((r) => r.id === idRole);
    return role?.name || '';
  }

  getContactInitials(dc: DealContactWithDetails): string {
    const first = dc.contactFullname?.charAt(0) || '';
    const last = dc.contactLastname?.charAt(0) || '';
    return (first + last).toUpperCase() || '?';
  }

  getContactAvatarColor(dc: DealContactWithDetails): string {
    const name = (dc.contactFullname || '') + (dc.contactLastname || '');
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }
    const colors = [
      'bg-blue-500',
      'bg-green-500',
      'bg-purple-500',
      'bg-orange-500',
      'bg-pink-500',
      'bg-teal-500',
      'bg-indigo-500',
      'bg-red-500',
    ];
    return colors[Math.abs(hash) % colors.length];
  }

  get isClosed(): boolean {
    return !!this.deal?.datetimeClosed;
  }

  get dealStatusBadgeColor(): BadgeColor {
    if (this.isClosed) {
      const cycleName = this.deal?.dealCycleName?.toLowerCase();
      if (cycleName?.includes('ganado') || cycleName?.includes('won')) {
        return 'success';
      }
      if (cycleName?.includes('perdido') || cycleName?.includes('lost')) {
        return 'error';
      }
      return 'light';
    }
    return 'info';
  }

  getCycleName(): string {
    if (!this.deal?.idDealCycle) return '—';
    return (
      this.deal.dealCycleName ||
      this.dealCycles.find((c) => c.id === this.deal?.idDealCycle)?.name ||
      '—'
    );
  }

  getBusinessTypeName(): string {
    if (!this.deal?.idDealBusinessType) return '';
    return (
      this.dealBusinessTypes.find(
        (bt) => bt.id === this.deal?.idDealBusinessType,
      )?.name || ''
    );
  }

  getPriorityColor(): string {
    if (this.deal?.priorityColor) return this.deal.priorityColor;
    if (!this.deal?.idDealPriority) return '';
    return (
      this.dealPriorities.find((p) => p.id === this.deal?.idDealPriority)
        ?.color || ''
    );
  }

  get ownerFullName(): string {
    if (!this.ownerUser) return '';
    return [this.ownerUser.fullname, this.ownerUser.lastname]
      .filter(Boolean)
      .join(' ');
  }

  formatCurrency(amount?: number): string {
    if (amount === undefined || amount === null) return '-';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: this.deal?.prefix,
      minimumFractionDigits: 2,
    }).format(amount);
  }

  formatDate(dateString?: string): string {
    return this.datetimeService.formatDateTime(dateString);
  }

  formatDateShort(dateString?: string): string {
    return this.datetimeService.formatDateShort(dateString);
  }

  // ── Timeline ──

  onTabChange(tab: 'info' | 'timeline' | 'quotes' | 'automatizaciones'): void {
    this.activeTab = tab;
    if (
      tab === 'timeline' &&
      this.timelineLogs.length === 0 &&
      !this.loadingTimeline
    ) {
      this.loadTimeline(0);
    }
    if (tab === 'quotes' && !this.quotesLoaded && !this.loadingQuotes && this.deal) {
      this.cargarCotizaciones(this.deal.id);
    }
  }

  // ── Cotizaciones del negocio ──

  cargarCotizaciones(dealId: number): void {
    const businessId = this.authService.getCurrentUser()?.businessId || 1;
    this.loadingQuotes = true;
    this.quotesError = '';
    this.quoteApi.getQuotesByDeal(dealId, businessId).subscribe({
      next: (quotes) => {
        this.dealQuotes = quotes || [];
        this.quotesLoaded = true;
        this.loadingQuotes = false;
      },
      error: () => {
        this.quotesError = 'Error al cargar las cotizaciones del negocio.';
        this.loadingQuotes = false;
      },
    });
  }

  // Abre el wizard de cotización con el negocio preseleccionado (alta rápida desde el detalle).
  nuevaCotizacion(): void {
    if (!this.deal) return;
    this.quoteFormModal.open({ dealId: this.deal.id });
  }

  // Abre el DETALLE de una cotización existente (no el wizard de edición).
  // Clic en la fila = consultar, igual que en la lista de cotizaciones; desde el
  // propio modal de detalle está el botón "Editar" que abre el wizard por encima.
  abrirCotizacion(quote: QuoteResponse): void {
    this.quoteDetailModal.open(quote.id);
  }

  // Monto total de una cotización con su propia moneda (distinta de la del negocio).
  formatQuoteAmount(quote: QuoteResponse): string {
    const formatted = new Intl.NumberFormat('es-PE', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(quote.totalAmount || 0);
    const prefix = quote.currencySymbol || quote.currencyCode || '';
    return prefix ? `${prefix} ${formatted}` : formatted;
  }

  loadTimeline(page = 0): void {
    if (!this.deal?.id) return;
    this.loadingTimeline = true;
    this.timelineError = '';
    this.timelinePage = page;

    this.auditApi
      .getLogs({
        entityName: 'deal',
        idEntity: String(this.deal.id),
        page,
        size: 15,
      })
      .subscribe({
        next: (data) => {
          this.timelineLogs = data.content ?? [];
          this.timelinePage = data.page ?? page;
          this.timelineTotalPages = data.totalPages ?? 1;
          this.timelineTotalElements = data.totalElements ?? 0;
          this.loadingTimeline = false;
        },
        error: () => {
          this.timelineError = 'Error al cargar la línea de tiempo.';
          this.loadingTimeline = false;
        },
      });
  }

  // La presentación de los eventos del timeline (traducciones, diffs, títulos)
  // y el expand/colapso por log viven en AuditTimelineComponent / AuditDisplayService.

  onClose(): void {
    this.close.emit();
  }
}
